import { Bot, ExternalLink, House, LoaderCircle, MessageCircle, SendHorizonal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { useSiteBranding } from "@/context/SiteContext";
import { API_BASE, api, formatApiErrorDetail } from "@/lib/api";
import { formatCategoryLabel, formatCurrency, formatListingPrice, formatNumber } from "@/lib/portalHelpers";
import { AdvisorConciergeHandoffPanel } from "./AdvisorConciergeHandoffPanel";
import { AdvisorComparisonStrategyPanel } from "./AdvisorComparisonStrategyPanel";
import { AdvisorDistrictIntelligencePanel } from "./AdvisorDistrictIntelligencePanel";
import { AdvisorInvestmentMemoPanel } from "./AdvisorInvestmentMemoPanel";
import { AdvisorInvestorThesisPanel } from "./AdvisorInvestorThesisPanel";
import { AdvisorMemoryProfileCard } from "./AdvisorMemoryProfileCard";
import { AdvisorObjectionActionBar } from "./AdvisorObjectionActionBar";
import { AdvisorScenarioSimulatorPanel } from "./AdvisorScenarioSimulatorPanel";
import { AdvisorShortlistCuratorCard } from "./AdvisorShortlistCuratorCard";
import {
  DISTRICT_SEGMENTS,
  VIEWING_TIME_SLOTS,
  buildListingAnchorSimilarProjects,
  buildComparisonInsights,
  buildJourneyReportContext,
  buildViewingTimeline,
  buildSimilarProjects,
  calculateRentVsBuySnapshot,
  calculateTaxSnapshot,
} from "./advisorHelpers";

const welcomeMessage = {
  role: "assistant",
  content: "Welcome to the SG Prop Portal. I am your Signature Advisor, grounded in the April 2026 Singapore market. How can I assist your wealth strategy today?",
};

const entryActions = [
  { key: "district", label: "📍 Choose District" },
  { key: "viewing", label: "📅 Private Viewing" },
  { key: "tax", label: "📊 Calculate 2026 ABSD/BSD" },
  { key: "rent", label: "🏠 Rent vs Buy Analysis" },
];

const toIsoDate = (date) => {
  if (!(date instanceof Date)) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildPremiumComparisonBundle = (anchor, alternatives = []) =>
  [anchor, ...alternatives]
    .filter((listing) => Boolean(listing?.id))
    .filter((listing, index, collection) => collection.findIndex((item) => item?.id === listing.id) === index);

const JOURNEY_POPUP_PANEL_MODES = new Set([
  "district-results",
  "listing-handoff-loading",
  "lead-form",
  "premium-comparison-ready",
  "post-modal-actions",
  "tax-form",
  "tax-result",
  "rent-form",
  "rent-result",
  "viewing-project",
  "viewing-form",
  "viewing-success",
]);

const INLINE_PANEL_MODES = new Set(["entry", "segment", "districts"]);

const getJourneyPopupMeta = (panelMode, districtLabel, districtIntroMessage, leadContext) => {
  switch (panelMode) {
    case "district-results":
      return {
        eyebrow: districtLabel ? `District Journey · ${districtLabel}` : "District Journey",
        title: "District shortlist ready",
        description: districtIntroMessage || "Your district shortlist is ready for review.",
      };
    case "listing-handoff-loading":
      return {
        eyebrow: "Comparison Journey",
        title: "Preparing your comparison shortlist",
        description: "The advisor is curating the strongest matched opportunities for this route.",
      };
    case "lead-form":
      if (leadContext?.mode === "memo") {
        return {
          eyebrow: districtLabel ? `Memo Journey · ${districtLabel}` : "Memo Journey",
          title: "Send your Signature Investment Memo",
          description: "Share your details to receive the memo PDF without leaving this docked panel.",
        };
      }
      if (leadContext?.mode === "concierge") {
        return {
          eyebrow: districtLabel ? `Concierge Journey · ${districtLabel}` : "Concierge Journey",
          title: "Prepare your concierge handoff",
          description: "Share your details so the advisor can log the handoff and prepare the WhatsApp brief for the team.",
        };
      }
      if (leadContext?.mode === "viewing") {
        return {
          eyebrow: districtLabel ? `Viewing Journey · ${districtLabel}` : "Viewing Journey",
          title: "Start your private viewing request",
          description: "Share your details so the advisor can continue into the project, date, and time selection without losing context.",
        };
      }
      return {
        eyebrow: "Unlock Journey",
        title: "Continue this advisor journey",
        description: "Share your contact details to unlock the next advisor asset without returning to the chat window.",
      };
    case "premium-comparison-ready":
      return {
        eyebrow: "Comparison Journey",
        title: "Premium comparison ready",
        description: "Open the curated comparison flow from here.",
      };
    case "post-modal-actions":
      return {
        eyebrow: "Next Step Journey",
        title: "Continue after comparison",
        description: "Move forward from the comparison flow without switching back into the chat panel.",
      };
    case "tax-form":
      return {
        eyebrow: "Tax Journey",
        title: "Calculate 2026 ABSD / BSD",
        description: "Complete the tax workflow inside this popup.",
      };
    case "tax-result":
      return {
        eyebrow: "Tax Journey",
        title: "Your tax view is ready",
        description: "Review the tax output here and continue the advisor journey from this popup.",
      };
    case "rent-form":
      return {
        eyebrow: "Rent vs Buy Journey",
        title: "Compare renting versus buying",
        description: "Complete the rent versus buy workflow inside this popup.",
      };
    case "rent-result":
      return {
        eyebrow: "Rent vs Buy Journey",
        title: "Your cost-of-waiting view is ready",
        description: "Review the result and continue the journey from this popup.",
      };
    case "viewing-project":
      return {
        eyebrow: "Viewing Journey",
        title: "Choose a listing for viewing",
        description: "Select the project you want to continue with inside this popup.",
      };
    case "viewing-form":
      return {
        eyebrow: "Viewing Journey",
        title: "Schedule a private viewing",
        description: "Pick the date and time without returning to the chat panel.",
      };
    case "viewing-success":
      return {
        eyebrow: "Viewing Journey",
        title: "Viewing request sent",
        description: "Your scheduling confirmation is now shown directly inside this popup.",
      };
    default:
      return {
        eyebrow: "Advisor Journey",
        title: "Continue advisor flow",
        description: "This advisor journey continues inside the popup window.",
      };
  }
};

const buildDistrictIntroMessage = ({ districtLabel, starBuyCount, isAgentPath, advisorName }) => {
  const safeDistrictLabel = districtLabel || "this district";
  const safeCount = Math.max(1, Number(starBuyCount || 0));
  if (isAgentPath) {
    const safeAdvisorName = advisorName || "your advisor";
    return `Excellent choice. ${safeAdvisorName} has shortlisted ${safeDistrictLabel} for its strong liveability, demand profile, and positioning story. Here are the top ${safeCount} 'Star Buys' to review together:`;
  }
  return `Excellent choice. ${safeDistrictLabel} is attracting investors looking for resilient demand, transformation upside, and selective entry points. Here are the top ${safeCount} 'Star Buys' currently available in our database:`;
};

const formatMessageHtml = (content) => {
  const escaped = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  const lines = escaped.split("\n");
  const parts = [];
  let listBuffer = [];

  const flushList = () => {
    if (!listBuffer.length) return;
    parts.push(`<ul style="margin:8px 0 0 18px;list-style:disc;">${listBuffer.join("")}</ul>`);
    listBuffer = [];
  };

  lines.forEach((line) => {
    if (line.trim().startsWith("- ")) {
      listBuffer.push(`<li style="margin:4px 0;">${line.trim().slice(2)}</li>`);
      return;
    }
    flushList();
    if (line.trim()) parts.push(`<p style="margin:0 0 8px;">${line}</p>`);
  });
  flushList();
  return parts.join("");
};

const MessageBubble = ({ item, index }) => (
  <div className={`flex ${item.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`signature-advisor-message-${index + 1}`}>
    <div
      className={`max-w-[90%] rounded-[22px] px-4 py-3 text-sm leading-7 shadow-sm ${
        item.role === "user" ? "bg-[#D4AF37] text-[#1A1A1A]" : "border border-[#D4AF37]/30 bg-white text-[#1A1A1A]"
      }`}
      dangerouslySetInnerHTML={{ __html: formatMessageHtml(item.content) }}
    />
  </div>
);

const ActionGridButton = ({ testId, label, onClick, variant = "outline", disabled = false }) => (
  <button
    className={`rounded-[18px] px-4 py-3 text-sm font-semibold transition ${
      variant === "solid"
        ? "bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#c79f29]"
        : variant === "emphasis"
          ? "border border-[#b88b10] bg-gradient-to-b from-[#E7C964] to-[#D4AF37] text-[#1A1A1A] shadow-[0_10px_24px_rgba(212,175,55,0.28)] hover:from-[#efd476] hover:to-[#cfa11f]"
        : "border border-[#D4AF37]/45 bg-white text-[#1A1A1A] hover:border-[#D4AF37] hover:bg-[#fff7e5]"
    } disabled:cursor-not-allowed disabled:opacity-60`}
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    {label}
  </button>
);

const ListingShowcaseCard = ({ listing, actionPrefix }) => {
  const { buildBrandedPath } = useSiteBranding();
  const coverImage = listing.image_url || listing.gallery_urls?.[0] || "";
  return (
    <div className="overflow-hidden rounded-[22px] border border-[#D4AF37]/30 bg-white shadow-[0_12px_30px_rgba(0,25,53,0.08)]" data-testid={`${actionPrefix}-card-${listing.id}`}>
      <div className="aspect-[1.5/1] overflow-hidden bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.18),transparent_40%),linear-gradient(180deg,#fffaf1_0%,#f3ead7_100%)]">
        {coverImage ? (
          <img alt={listing.title} className="h-full w-full object-cover" data-testid={`${actionPrefix}-image-${listing.id}`} decoding="async" loading="lazy" src={coverImage} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-[#8b6d16]" data-testid={`${actionPrefix}-image-fallback-${listing.id}`}>
            Listing visual pending
          </div>
        )}
      </div>
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-[#D4AF37]/35 bg-[#fff4db] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`${actionPrefix}-category-${listing.id}`}>
            {formatCategoryLabel(listing.category)}
          </span>
          <span className="rounded-full border border-[#001935]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`${actionPrefix}-district-${listing.id}`}>
            {listing.district}
          </span>
        </div>
        <div>
          <p className="text-base font-semibold text-[#001935]" data-testid={`${actionPrefix}-title-${listing.id}`}>{listing.title}</p>
          <p className="mt-1 text-sm font-medium text-[#8b6d16]" data-testid={`${actionPrefix}-price-${listing.id}`}>{formatListingPrice(listing)}</p>
          <p className="mt-2 text-sm leading-6 text-[#5f5a4e]" data-testid={`${actionPrefix}-meta-${listing.id}`}>
            {formatNumber(listing.sqft || 0)} sqft • {listing.property_type} • {listing.status}
          </p>
        </div>
        <Link
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#001935] underline-offset-4 hover:underline"
          data-testid={`${actionPrefix}-view-link-${listing.id}`}
          rel="noreferrer"
          target="_blank"
          to={buildBrandedPath(`/listings/${listing.id}`)}
        >
          Open project page <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};

export const SignatureAdvisorWidget = ({ forceRouteExperience = false }) => {
  const navigate = useNavigate();
  const { branding, buildBrandedPath, getWhatsAppHref, isAgentPath, requestParams } = useSiteBranding();
  const activeAgentSlug = isAgentPath ? branding.subdomain_slug : undefined;
  const hideFloatingLauncher = forceRouteExperience;
  const [open, setOpen] = useState(false);
  const [booting, setBooting] = useState(false);
  const [sending, setSending] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [viewingSubmitting, setViewingSubmitting] = useState(false);
  const [viewingSuccessMessage, setViewingSuccessMessage] = useState("");
  const [districtIntroMessage, setDistrictIntroMessage] = useState("");
  const [advisorProfile, setAdvisorProfile] = useState(null);
  const [comparisonStrategy, setComparisonStrategy] = useState(null);
  const [shortlistCurator, setShortlistCurator] = useState(null);
  const [districtIntelligence, setDistrictIntelligence] = useState(null);
  const [investorThesis, setInvestorThesis] = useState(null);
  const [investmentMemo, setInvestmentMemo] = useState(null);
  const [scenarioSimulator, setScenarioSimulator] = useState(null);
  const [selectedScenarioKey, setSelectedScenarioKey] = useState("");
  const [conciergeHandoff, setConciergeHandoff] = useState(null);
  const [objectionResponse, setObjectionResponse] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([welcomeMessage]);
  const [prompt, setPrompt] = useState("");
  const [inputUnlocked, setInputUnlocked] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [districtLoadingCode, setDistrictLoadingCode] = useState("");
  const [panelMode, setPanelMode] = useState("entry");
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [starBuys, setStarBuys] = useState([]);
  const [similarProjects, setSimilarProjects] = useState([]);
  const [comparisonInsights, setComparisonInsights] = useState([]);
  const [revealedMode, setRevealedMode] = useState("");
  const [leadContext, setLeadContext] = useState(null);
  const [capturedLead, setCapturedLead] = useState(null);
  const [conciergeSubmitting, setConciergeSubmitting] = useState(false);
  const [conciergePrepared, setConciergePrepared] = useState(false);
  const [preparedHandoffMessage, setPreparedHandoffMessage] = useState("");
  const [mobileLauncherLayout, setMobileLauncherLayout] = useState({ anchor: "right", bottomOffset: 16, isMobile: false });
  const [chosenViewingProject, setChosenViewingProject] = useState(null);
  const [anchorListing, setAnchorListing] = useState(null);
  const [premiumComparisonProjects, setPremiumComparisonProjects] = useState([]);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", whatsapp: "" });
  const [schedulingRequest, setSchedulingRequest] = useState({ selectedDate: null, preferred_date: "", time_window: "", specific_time: "", custom_time: "" });
  const [taxForm, setTaxForm] = useState({ residency: "SC", property_count: 1, purchase_price: "" });
  const [taxSnapshot, setTaxSnapshot] = useState(null);
  const [rentForm, setRentForm] = useState({ monthly_rent: "", budget: "" });
  const [rentSnapshot, setRentSnapshot] = useState(null);
  const messagesPanelRef = useRef(null);
  const memoryHydrationRef = useRef("");

  useEffect(() => {
    if (forceRouteExperience) {
      setOpen(true);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("advisor") === "open") setOpen(true);
    try {
      const storedLead = window.localStorage.getItem("signature-advisor-captured-lead");
      if (storedLead) {
        const parsedLead = JSON.parse(storedLead);
        setCapturedLead(parsedLead);
        setLeadForm({
          name: parsedLead.name || "",
          email: parsedLead.email || "",
          whatsapp: parsedLead.whatsapp || parsedLead.phone || "",
        });
      }
    } catch {
      // ignore invalid stored lead payload
    }
  }, [forceRouteExperience]);

  useEffect(() => {
    if (!messagesPanelRef.current) return;
    messagesPanelRef.current.scrollTop = messagesPanelRef.current.scrollHeight;
  }, [messages, panelMode, schedulingRequest.preferred_date, schedulingRequest.time_window]);

  const appendMessage = (role, content) => {
    setMessages((current) => [...current, { role, content }]);
  };

  const transcriptMessages = useMemo(() => messages.map((item) => ({ role: item.role, content: item.content })), [messages]);
  const viewingProjectOptions = useMemo(() => {
    const seen = new Set();
    return [chosenViewingProject, anchorListing, ...starBuys, ...(inventory || [])]
      .filter((listing) => Boolean(listing?.id) && Boolean(listing?.title))
      .filter((listing) => {
        if (seen.has(listing.id)) return false;
        seen.add(listing.id);
        return true;
      })
      .slice(0, 12);
  }, [anchorListing, chosenViewingProject, inventory, starBuys]);

  const hydrateAdvisorSession = (payload, { withMemoryMessage = false } = {}) => {
    if (!payload) return;
    setAdvisorProfile(payload.advisor_profile || null);
    setComparisonStrategy(payload.comparison_strategy || null);
    setShortlistCurator(payload.shortlist_curator || null);
    setDistrictIntelligence(payload.district_intelligence || null);
    setInvestorThesis(payload.investor_thesis || null);
    setInvestmentMemo(payload.investment_memo || null);
    setScenarioSimulator(payload.scenario_simulator || null);
    setSelectedScenarioKey((current) => {
      const fallbackKey = payload.scenario_simulator?.default_scenario_key || "";
      const stillValid = payload.scenario_simulator?.scenarios?.some((item) => item.key === current);
      return stillValid ? current : fallbackKey;
    });
    setConciergeHandoff(payload.concierge_handoff || null);
    if (withMemoryMessage && payload.memory_summary && memoryHydrationRef.current !== payload.session_id) {
      setMessages((current) => {
        if (current.some((item) => item.role === "assistant" && item.content === payload.memory_summary)) return current;
        return [...current, { role: "assistant", content: payload.memory_summary }];
      });
      setInputUnlocked(true);
      memoryHydrationRef.current = payload.session_id;
    }
  };

  const ensureSession = async () => {
    if (sessionId) {
      return {
        session_id: sessionId,
        advisor_profile: advisorProfile,
        comparison_strategy: comparisonStrategy,
        shortlist_curator: shortlistCurator,
        district_intelligence: districtIntelligence,
        investor_thesis: investorThesis,
        investment_memo: investmentMemo,
        scenario_simulator: scenarioSimulator,
        concierge_handoff: conciergeHandoff,
      };
    }
    setBooting(true);
    try {
      const storedSessionId = window.localStorage.getItem("signature-advisor-session-id");
      const response = await api.post("/ai/signature-advisor/session", { session_id: storedSessionId || undefined, agent_slug: activeAgentSlug });
      window.localStorage.setItem("signature-advisor-session-id", response.data.session_id);
      setSessionId(response.data.session_id);
      hydrateAdvisorSession(response.data, { withMemoryMessage: Boolean(storedSessionId) });
      return response.data;
    } finally {
      setBooting(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    ensureSession().catch(() => null);
  }, [open]);

  const ensureInventory = async () => {
    if (inventory) return inventory;
    setInventoryLoading(true);
    try {
      const response = await api.get("/listings", { params: { limit: 500, ...requestParams } });
      setInventory(response.data);
      return response.data;
    } finally {
      setInventoryLoading(false);
    }
  };

  const resetJourneyVisuals = () => {
    setSelectedDistrict(null);
    setSelectedSegment(null);
    setDistrictIntroMessage("");
    setComparisonStrategy(null);
    setShortlistCurator(null);
    setDistrictIntelligence(null);
    setInvestorThesis(null);
    setInvestmentMemo(null);
    setScenarioSimulator(null);
    setSelectedScenarioKey("");
    setConciergeHandoff(null);
    setConciergePrepared(false);
    setPreparedHandoffMessage("");
    setStarBuys([]);
    setSimilarProjects([]);
    setComparisonInsights([]);
    setRevealedMode("");
    setLeadContext(null);
    setChosenViewingProject(null);
    setAnchorListing(null);
    setPremiumComparisonProjects([]);
    setSchedulingRequest({ selectedDate: null, preferred_date: "", time_window: "", specific_time: "", custom_time: "" });
    setTaxSnapshot(null);
    setRentSnapshot(null);
  };

  const openListingWarmHandoff = useCallback(async (detail) => {
    const listingContext = detail?.listing;
    if (!listingContext?.title || !listingContext?.district) return;

    resetJourneyVisuals();
    setOpen(true);
    setInputUnlocked(true);
    setMessages([
      {
        role: "assistant",
        content: `I see you're looking at ${listingContext.title}. I’ve identified 3 other high-potential units in ${listingContext.district} with similar investment profiles. Would you like to see the side-by-side comparison?`,
      },
      {
        role: "assistant",
        content: "To unlock the Premium Comparison experience, please let me know where to send the tailored report.",
      },
    ]);
    setSelectedDistrict({ code: listingContext.district, label: listingContext.district });
    setAnchorListing(listingContext);
    setDistrictLoadingCode(listingContext.district);
    setPanelMode("listing-handoff-loading");

    try {
      const sessionPayload = await ensureSession();
      const activeSessionId = sessionPayload.session_id;
      const [allListings] = await Promise.all([
        ensureInventory(),
        api.post("/ai/signature-advisor/district-discovery", { session_id: activeSessionId, district_code: listingContext.district, agent_slug: activeAgentSlug }).catch(() => null),
        new Promise((resolve) => window.setTimeout(resolve, 350)),
      ]);
      const anchoredSimilarUnits = buildListingAnchorSimilarProjects(allListings, listingContext);
      setStarBuys(anchoredSimilarUnits);
      setSimilarProjects(anchoredSimilarUnits);
      setComparisonInsights(buildComparisonInsights([listingContext, ...anchoredSimilarUnits]));
      setLeadContext({
        mode: "listing-modal",
        reportType: "similar_projects_report",
        contextLabel: `${listingContext.title} Similar Units`,
        selectedProjects: [listingContext, ...anchoredSimilarUnits].map((listing) => listing.title).filter(Boolean),
        comparisonContext: {
          district_code: listingContext.district,
          district_label: listingContext.district,
          anchor_project: listingContext.title,
          compared_projects: anchoredSimilarUnits.map((listing) => listing.title),
          revealed_similar_projects: anchoredSimilarUnits.map((listing) => listing.title),
        },
        blt: {
          budget: "Not provided",
          location: listingContext.district,
          timeline: "Warm handoff from listing detail page",
        },
      });
      setPanelMode("lead-form");
    } catch {
      setMessages([
        {
          role: "assistant",
          content: `I see you're looking at ${listingContext.title}. I’m having trouble loading similar units right now. Please try again in a moment.`,
        },
      ]);
      setPanelMode(null);
    } finally {
      setDistrictLoadingCode("");
    }
  }, [ensureInventory, ensureSession]);

  useEffect(() => {
    const handler = (event) => {
      openListingWarmHandoff(event.detail);
    };
    window.addEventListener("signature-advisor-open", handler);
    return () => window.removeEventListener("signature-advisor-open", handler);
  }, [openListingWarmHandoff]);

  useEffect(() => {
    const closeHandler = () => {
      setOpen(true);
      setPanelMode("post-modal-actions");
    };
    const requestViewingHandler = async (event) => {
      setOpen(true);
      await beginViewingRequest(event.detail?.listing || null);
    };

    window.addEventListener("signature-advisor-comparison-modal-closed", closeHandler);
    window.addEventListener("signature-advisor-start-viewing", requestViewingHandler);
    window.addEventListener("signature-advisor-comparison-request-viewing", requestViewingHandler);
    return () => {
      window.removeEventListener("signature-advisor-comparison-modal-closed", closeHandler);
      window.removeEventListener("signature-advisor-start-viewing", requestViewingHandler);
      window.removeEventListener("signature-advisor-comparison-request-viewing", requestViewingHandler);
    };
  }, [beginViewingRequest]);

  const sendPrompt = async (forcedPrompt = null) => {
    const currentPrompt = (forcedPrompt ?? prompt).trim();
    if (!currentPrompt) return;
    if (!forcedPrompt) setPrompt("");
    appendMessage("user", currentPrompt);
    setObjectionResponse(null);
    setSending(true);
    try {
      const sessionPayload = await ensureSession();
      const activeSessionId = sessionPayload.session_id;
      const contextPrefix = [selectedDistrict?.label, leadContext?.contextLabel, chosenViewingProject?.title].filter(Boolean).slice(0, 1).join(" — ");
      const backendPrompt = contextPrefix ? `${contextPrefix}\nUser question: ${currentPrompt}` : currentPrompt;
      const response = await api.post("/ai/signature-advisor/message", { session_id: activeSessionId, message: backendPrompt, agent_slug: activeAgentSlug });
      appendMessage("assistant", response.data.reply);
      hydrateAdvisorSession(response.data);
      setObjectionResponse(response.data.objection_response || null);
      setInputUnlocked(true);
    } catch (error) {
      appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleObjectionQuickAction = (action) => {
    setPrompt("");
    sendPrompt(action);
  };

  const startDistrictJourney = async () => {
    appendMessage("user", "📍 Choose District");
    appendMessage("assistant", "Which market segment are you exploring?");
    setInputUnlocked(true);
    setPanelMode("segment");
    await ensureInventory().catch(() => appendMessage("assistant", "I’m unable to load the district inventory right now. Please try again shortly."));
  };

  const handlePrimaryAction = async (actionKey) => {
    resetJourneyVisuals();
    if (actionKey === "district") {
      await startDistrictJourney();
      return;
    }
    if (actionKey === "viewing") {
      await beginViewingRequest();
      return;
    }
    if (actionKey === "tax") {
      appendMessage("user", "📊 Calculate 2026 ABSD/BSD");
      appendMessage("assistant", "Share your residency status and current property count. If you want a sharper estimate, you can also add an indicative purchase price.");
      setInputUnlocked(true);
      setPanelMode("tax-form");
      return;
    }
    appendMessage("user", "🏠 Rent vs Buy Analysis");
    appendMessage("assistant", "Tell me your current monthly rent and the purchase budget you are evaluating, and I’ll show you the 2026 cost-of-waiting frame.");
    setInputUnlocked(true);
    setPanelMode("rent-form");
  };

  const handleSegmentSelect = (segment) => {
    appendMessage("user", segment.label);
    appendMessage("assistant", `Select a district within ${segment.label}.`);
    setSelectedSegment(segment);
    setPanelMode("districts");
  };

  const handleDistrictSelect = async (district) => {
    appendMessage("user", district.label);
    setDistrictLoadingCode(district.code);
    setSelectedDistrict(district);
    try {
      const sessionPayload = await ensureSession();
      const activeSessionId = sessionPayload.session_id;
      const [districtResponse, listings] = await Promise.all([
        api.post("/ai/signature-advisor/district-discovery", { session_id: activeSessionId, district_code: district.code, agent_slug: activeAgentSlug }),
        ensureInventory(),
        new Promise((resolve) => window.setTimeout(resolve, 500)),
      ]);
      const topStarBuys = districtResponse.data.listings || [];
      const alternatives = buildSimilarProjects(listings, topStarBuys, district.code);
      const comparisons = buildComparisonInsights(topStarBuys);

      setStarBuys(topStarBuys);
      setSimilarProjects(alternatives);
      setComparisonInsights(comparisons);
      setAdvisorProfile(districtResponse.data.advisor_profile || null);
      setComparisonStrategy(districtResponse.data.comparison_strategy || null);
      setShortlistCurator(districtResponse.data.shortlist_curator || null);
      setDistrictIntelligence(districtResponse.data.district_intelligence || null);
      setInvestorThesis(districtResponse.data.investor_thesis || null);
      setInvestmentMemo(districtResponse.data.investment_memo || null);
      setScenarioSimulator(districtResponse.data.scenario_simulator || null);
      setSelectedScenarioKey(districtResponse.data.scenario_simulator?.default_scenario_key || "");
      setConciergeHandoff(districtResponse.data.concierge_handoff || null);
      setConciergePrepared(false);
      setPreparedHandoffMessage("");
      setObjectionResponse(null);
      setRevealedMode("");
      setDistrictIntroMessage(buildDistrictIntroMessage({
        districtLabel: district.label,
        starBuyCount: topStarBuys.length || 3,
        isAgentPath,
        advisorName: branding.name,
      }));
      setPanelMode("district-results");
    } catch {
      appendMessage("assistant", "I couldn’t load the district inventory just now. Please try again in a moment.");
    } finally {
      setDistrictLoadingCode("");
    }
  };

  const revealDiscovery = (mode) => {
    setRevealedMode(mode);
    appendMessage("assistant", mode === "similar" ? "Here are the three strongest adjacent alternatives from our live unified inventory." : "Here is your unlocked comparison report across the current district shortlist.");
    setPanelMode("district-results");
  };

  const openLeadGate = (mode) => {
    const reportType = mode === "similar" ? "similar_projects_report" : "district_comparison_report";
    const districtLabel = selectedDistrict?.label || "District discovery";
    const journeyReport = buildJourneyReportContext({ mode, districtLabel, starBuys, similarProjects, anchorListing });
    if (!journeyReport.selectedProjects.length) {
      appendMessage("assistant", "Which project should I include in your report?");
      return;
    }
    const nextLeadContext = {
      mode,
      reportType,
      contextLabel: journeyReport.contextLabel,
      selectedProjects: journeyReport.selectedProjects,
      comparisonContext: journeyReport.comparisonContext,
      blt: {
        budget: rentForm.budget || taxForm.purchase_price || "Not provided",
        location: districtLabel,
        timeline: mode === "similar" ? "Exploring alternatives" : "Comparing shortlisted projects",
      },
    };
    appendMessage("user", mode === "similar" ? "🔍 Find similar projects" : "⚖️ Compare these projects");
    if (capturedLead) {
      setLeadContext(nextLeadContext);
      setLeadSubmitting(true);
      requestLeadReport(nextLeadContext, capturedLead)
        .then((response) => appendMessage("assistant", response.data.message))
        .catch((error) => appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail)))
        .finally(() => setLeadSubmitting(false));
      revealDiscovery(mode);
      return;
    }
    appendMessage(
      "assistant",
      "I have identified 3 high-potential alternatives and a detailed comparison report. To unlock these insights and receive the PDF version, please let me know where to send them:",
    );
    setLeadContext(nextLeadContext);
    setPanelMode("lead-form");
  };

  const requestLeadReport = async (context, contact) => {
    const sessionPayload = await ensureSession();
    const activeSessionId = sessionPayload.session_id;
    const payload = {
      session_id: activeSessionId,
      name: contact.name,
      email: contact.email,
      whatsapp: contact.whatsapp,
      report_type: context.reportType,
      context_label: context.contextLabel,
      selected_projects: context.selectedProjects,
      comparison_context: context.comparisonContext || {},
      blt: context.blt,
      transcript_messages: transcriptMessages,
    };
    return api.post("/ai/signature-advisor/lead", payload);
  };

  const buildMemoLeadContext = () => {
    const districtLabel = selectedDistrict?.label || anchorListing?.district || "Singapore property market";
    const projects = buildPremiumComparisonBundle(anchorListing, starBuys).slice(0, 3);
    return {
      mode: "memo",
      reportType: "investment_forecast_report",
      contextLabel: investmentMemo?.subject || `${districtLabel} Signature Investment Memo`,
      selectedProjects: projects.map((listing) => listing.title).filter(Boolean),
      comparisonContext: {
        district_code: selectedDistrict?.code || anchorListing?.district || "",
        district_label: districtLabel,
        journey_mode: "investment_memo",
        anchor_project: anchorListing?.title || projects[0]?.title,
        compared_projects: projects.map((listing) => listing.title).filter(Boolean),
      },
      blt: {
        budget: advisorProfile?.budget_band || rentForm.budget || taxForm.purchase_price || "Not provided",
        location: districtLabel,
        timeline: advisorProfile?.timeline || "Investment memo review",
      },
    };
  };

  const openMemoLeadGate = async () => {
    appendMessage("user", "🧠 Generate my Signature Investment Memo");
    const nextLeadContext = buildMemoLeadContext();
    if (capturedLead) {
      setLeadSubmitting(true);
      try {
        const response = await requestLeadReport(nextLeadContext, capturedLead);
        appendMessage("assistant", response.data.message);
      } catch (error) {
        appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
      } finally {
        setLeadSubmitting(false);
      }
      return;
    }
    appendMessage("assistant", "To send your Signature Investment Memo as a PDF, please share your details below.");
    setLeadContext(nextLeadContext);
    setPanelMode("lead-form");
  };

  const prepareConciergeHandoff = async (contactOverride = null) => {
    const sessionPayload = await ensureSession();
    const activeSessionId = sessionPayload.session_id;
    const payload = {
      session_id: activeSessionId,
      agent_slug: activeAgentSlug,
      ...(contactOverride
        ? {
            name: contactOverride.name,
            email: contactOverride.email,
            whatsapp: contactOverride.whatsapp,
          }
        : {}),
    };
    const response = await api.post("/ai/signature-advisor/concierge-handoff", payload);
    setConciergePrepared(Boolean(response.data.admin_logged));
    setPreparedHandoffMessage(response.data.whatsapp_message || "");
    appendMessage("assistant", response.data.message);
    return response;
  };

  const openConciergeHandoff = async () => {
    appendMessage("user", "🤝 Prepare concierge handoff");
    if (capturedLead) {
      setConciergeSubmitting(true);
      try {
        await prepareConciergeHandoff();
      } catch (error) {
        appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
      } finally {
        setConciergeSubmitting(false);
      }
      return;
    }
    appendMessage("assistant", "Share your details so I can prepare the concierge brief and log it for the team.");
    setLeadContext({ mode: "concierge" });
    setPanelMode("lead-form");
  };

  const submitLeadCapture = async (event) => {
    event.preventDefault();
    if (!leadContext) return;
    setLeadSubmitting(true);
    try {
      const contact = { name: leadForm.name, email: leadForm.email, whatsapp: leadForm.whatsapp };
      setCapturedLead({ name: leadForm.name, email: leadForm.email, whatsapp: leadForm.whatsapp });
      window.localStorage.setItem("signature-advisor-captured-lead", JSON.stringify(contact));
      if (leadContext.mode === "concierge") {
        const response = await prepareConciergeHandoff(contact);
        setConciergeHandoff((current) => (current ? { ...current, whatsapp_message: response.data.whatsapp_message || current.whatsapp_message } : current));
        setLeadContext(null);
        setPanelMode("district-results");
      } else if (leadContext.mode === "viewing") {
        const nextViewingProject = leadContext.viewingProject || null;
        setLeadContext(null);
        if (nextViewingProject) {
          setChosenViewingProject(nextViewingProject);
          appendMessage("assistant", `Great — now choose a calendar date first, then select AM or PM and the exact time slot for ${nextViewingProject.title}.`);
          setPanelMode("viewing-form");
        } else {
          await ensureInventory().catch(() => null);
          appendMessage("assistant", "Great — now choose the project you would like us to prioritise for your private viewing request.");
          setPanelMode("viewing-project");
        }
      } else if (leadContext.mode === "listing-modal") {
        const comparisonBundle = buildPremiumComparisonBundle(anchorListing, starBuys);
        setPremiumComparisonProjects(comparisonBundle);
        if (anchorListing?.id) {
          window.sessionStorage.setItem(`signature-premium-comparison:${anchorListing.id}`, JSON.stringify({ listingId: anchorListing.id, listings: comparisonBundle }));
        }
        appendMessage(
          "assistant",
          `Thank you. I have prepared your personalized comparison of ${anchorListing?.title || "this project"} and 2 high-potential alternatives in ${anchorListing?.district || selectedDistrict?.label || "the district"}.`,
        );
        setPanelMode("premium-comparison-ready");
        requestLeadReport(leadContext, contact).catch((error) => {
          appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail || "Your comparison is ready, but the PDF send is taking longer than expected."));
        });
      } else if (leadContext.mode === "similar" || leadContext.mode === "compare") {
        const response = await requestLeadReport(leadContext, contact);
        appendMessage("assistant", response.data.message);
        setLeadContext(null);
        revealDiscovery(leadContext.mode);
      } else if (leadContext.mode === "memo") {
        const response = await requestLeadReport(leadContext, contact);
        appendMessage("assistant", response.data.message);
        setLeadContext(null);
        setPanelMode("district-results");
      } else {
        const response = await requestLeadReport(leadContext, contact);
        appendMessage("assistant", response.data.message);
        setLeadContext(null);
        setPanelMode(leadContext.mode === "tax" ? "tax-result" : "rent-result");
      }
    } catch (error) {
      appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLeadSubmitting(false);
    }
  };

  const submitTaxSnapshot = () => {
    const snapshot = calculateTaxSnapshot({
      residency: taxForm.residency,
      propertyCount: Number(taxForm.property_count || 1),
      purchasePrice: Number(taxForm.purchase_price || 0),
    });
    setTaxSnapshot(snapshot);
    appendMessage(
      "user",
      `Residency: ${taxForm.residency} • Property Count: ${taxForm.property_count}${taxForm.purchase_price ? ` • Purchase Price: ${formatCurrency(taxForm.purchase_price)}` : ""}`,
    );
    appendMessage(
      "assistant",
      `For April 2026, your ABSD rate is **${snapshot.absdRate}%**. BSD remains progressive up to **6%** above SGD 3M${Number(taxForm.purchase_price || 0) > 0 ? `, and your indicative BSD is **${formatCurrency(snapshot.bsdDuty)}**` : ""}.`,
    );
    setPanelMode("tax-result");
  };

  const unlockTaxReport = async () => {
    if (capturedLead) {
      setLeadSubmitting(true);
      try {
        const existingContext = {
          mode: "tax",
          reportType: "tax_optimization_pdf",
          contextLabel: "2026 Tax Optimization PDF",
          selectedProjects: [],
          blt: {
            budget: taxForm.purchase_price ? formatCurrency(taxForm.purchase_price) : "Not provided",
            location: selectedDistrict?.label || "Singapore",
            timeline: "Tax planning stage",
          },
        };
        const response = await requestLeadReport(existingContext, capturedLead);
        appendMessage("assistant", response.data.message);
      } catch (error) {
        appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
      } finally {
        setLeadSubmitting(false);
      }
      return;
    }
    appendMessage("user", "Unlock Tax Optimization PDF");
    appendMessage("assistant", "To send your Tax Optimization PDF, please share your details below.");
    setLeadContext({
      mode: "tax",
      reportType: "tax_optimization_pdf",
      contextLabel: "2026 Tax Optimization PDF",
      selectedProjects: [],
      blt: {
        budget: taxForm.purchase_price ? formatCurrency(taxForm.purchase_price) : "Not provided",
        location: selectedDistrict?.label || "Singapore",
        timeline: "Tax planning stage",
      },
    });
    setPanelMode("lead-form");
  };

  const submitRentVsBuy = () => {
    const snapshot = calculateRentVsBuySnapshot({ monthlyRent: Number(rentForm.monthly_rent || 0), budget: Number(rentForm.budget || 0) });
    setRentSnapshot(snapshot);
    appendMessage("user", `Monthly Rent: ${formatCurrency(rentForm.monthly_rent || 0)} • Budget: ${formatCurrency(rentForm.budget || 0)}`);
    appendMessage(
      "assistant",
      `At April 2026 rates of **2.8%–3.2%**, a 75% loan on this budget sits around **${formatCurrency(snapshot.lowRatePayment)} to ${formatCurrency(snapshot.highRatePayment)} / month**. Waiting another year at your current rent burns roughly **${formatCurrency(snapshot.annualRentSpend)}** in outflow before any ownership upside is captured.`,
    );
    setPanelMode("rent-result");
  };

  const unlockWealthReport = () => {
    if (capturedLead) {
      setLeadSubmitting(true);
      requestLeadReport(
        {
          mode: "rent",
          reportType: "wealth_accumulation_report",
          contextLabel: "Wealth Accumulation Report",
          selectedProjects: [],
          blt: {
            budget: rentForm.budget ? formatCurrency(rentForm.budget) : "Not provided",
            location: selectedDistrict?.label || "Singapore",
            timeline: "Rent vs Buy review",
          },
        },
        capturedLead,
      )
        .then((response) => appendMessage("assistant", response.data.message))
        .catch((error) => appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail)))
        .finally(() => setLeadSubmitting(false));
      return;
    }
    appendMessage("user", "Unlock Wealth Accumulation Report");
    appendMessage("assistant", "To send your Wealth Accumulation Report, please share your details below.");
    setLeadContext({
      mode: "rent",
      reportType: "wealth_accumulation_report",
      contextLabel: "Wealth Accumulation Report",
      selectedProjects: [],
      blt: {
        budget: rentForm.budget ? formatCurrency(rentForm.budget) : "Not provided",
        location: selectedDistrict?.label || "Singapore",
        timeline: "Rent vs Buy review",
      },
    });
    setPanelMode("lead-form");
  };

  async function beginViewingRequest(prefilledProject = null) {
    setViewingSuccessMessage("");
    setSchedulingRequest({ selectedDate: null, preferred_date: "", time_window: "", specific_time: "", custom_time: "" });
    if (!capturedLead) {
      appendMessage("user", prefilledProject?.title ? `📅 Request viewing for ${prefilledProject.title}` : "📅 Schedule a Private Viewing");
      appendMessage("assistant", "Share your details first, and I’ll help you lock in the project, date, and time for the private viewing request.");
      setLeadContext({
        mode: "viewing",
        contextLabel: prefilledProject?.title || selectedDistrict?.label || "Private Viewing",
        selectedProjects: prefilledProject?.title ? [prefilledProject.title] : [],
        viewingProject: prefilledProject || null,
        blt: {
          budget: rentForm.budget || taxForm.purchase_price || "Not provided",
          location: selectedDistrict?.label || prefilledProject?.district || "Singapore",
          timeline: "Private viewing request",
        },
      });
      setPanelMode("lead-form");
      return;
    }

    if (prefilledProject) {
      setChosenViewingProject(prefilledProject);
      appendMessage("user", `📅 Request viewing for ${prefilledProject.title}`);
      appendMessage("assistant", `Choose a calendar date first, then select AM or PM and the exact time slot for ${prefilledProject.title}.`);
      setPanelMode("viewing-form");
      return;
    }

    await ensureInventory().catch(() => appendMessage("assistant", "I’m unable to load the project inventory right now. You can still continue with a general private viewing request."));
    appendMessage("user", "📅 Schedule a Private Viewing");
    appendMessage("assistant", "Which project should our team prioritise for a private viewing request?");
    setPanelMode("viewing-project");
  }

  const chooseViewingProject = (listing) => {
    setViewingSuccessMessage("");
    setChosenViewingProject(listing);
    setSchedulingRequest({ selectedDate: null, preferred_date: "", time_window: "", specific_time: "", custom_time: "" });
    appendMessage("user", listing.title);
    appendMessage("assistant", `Choose a calendar date first, then select AM or PM and the exact time slot for ${listing.title}.`);
    setPanelMode("viewing-form");
  };

  const submitViewingRequest = async (event) => {
    event.preventDefault();
    if (!capturedLead || !chosenViewingProject) return;
    const specificTime = schedulingRequest.custom_time.trim() || schedulingRequest.specific_time;
    if (!schedulingRequest.preferred_date || !schedulingRequest.time_window || !specificTime) {
      appendMessage("assistant", "Please choose a calendar date, then pick AM or PM and the exact time slot before confirming.");
      return;
    }
    setViewingSubmitting(true);
    try {
      const sessionPayload = await ensureSession();
      const activeSessionId = sessionPayload.session_id;
      const payload = {
        session_id: activeSessionId,
        project_name: chosenViewingProject.title,
        preferred_date: schedulingRequest.preferred_date,
        time_window: schedulingRequest.time_window,
        specific_time: specificTime,
        name: capturedLead.name,
        email: capturedLead.email,
        whatsapp: capturedLead.whatsapp,
        context_label: selectedDistrict?.label || chosenViewingProject.title,
        blt: {
          budget: rentForm.budget || taxForm.purchase_price || "Not provided",
          location: selectedDistrict?.label || chosenViewingProject.district,
          timeline: buildViewingTimeline({
            preferredDate: schedulingRequest.preferred_date,
            timeWindow: schedulingRequest.time_window,
            specificTime: schedulingRequest.specific_time,
            customTime: schedulingRequest.custom_time,
          }),
        },
        transcript_messages: transcriptMessages,
      };
      const response = await api.post("/ai/signature-advisor/viewing-request", payload);
      appendMessage("assistant", response.data.message);
      setViewingSuccessMessage(response.data.message);
      setPanelMode("viewing-success");
    } catch (error) {
      appendMessage("assistant", formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setViewingSubmitting(false);
    }
  };

  const renderEntryButtons = () => (
    <div className="grid gap-3" data-testid="signature-advisor-entry-actions">
      {entryActions.map((action) => (
        <ActionGridButton
          key={action.key}
          label={action.label}
          onClick={() => handlePrimaryAction(action.key)}
          testId={`signature-advisor-entry-${action.key}-button`}
          variant="solid"
        />
      ))}
    </div>
  );

  const renderSegmentPicker = () => (
    <div className="grid gap-3" data-testid="signature-advisor-segment-picker">
      {DISTRICT_SEGMENTS.map((segment) => (
        <ActionGridButton
          key={segment.key}
          label={segment.label}
          onClick={() => handleSegmentSelect(segment)}
          testId={`signature-advisor-segment-${segment.key.toLowerCase()}-button`}
        />
      ))}
    </div>
  );

  const renderDistrictPicker = () => (
    <div className="space-y-3" data-testid="signature-advisor-district-picker">
      <div className="rounded-[18px] border border-[#D4AF37]/25 bg-[#fff7e5] px-4 py-3 text-sm font-medium text-[#6b6252]" data-testid="signature-advisor-selected-segment-pill">
        {selectedSegment?.label}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {selectedSegment?.districts.map((district) => (
          <ActionGridButton
            key={district.code}
            label={district.label}
            onClick={() => handleDistrictSelect(district)}
            disabled={Boolean(districtLoadingCode)}
            testId={`signature-advisor-district-${district.code.toLowerCase()}-button`}
          />
        ))}
      </div>
      {districtLoadingCode ? (
        <div className="flex items-center gap-3 rounded-[18px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm font-medium text-[#6b6252]" data-testid="signature-advisor-district-searching-state">
          <LoaderCircle className="h-4 w-4 animate-spin text-[#8b6d16]" />
          Searching {districtLoadingCode} for live Star Buys...
        </div>
      ) : null}
    </div>
  );

  const renderDistrictResults = () => (
    <div className="space-y-6" data-testid="signature-advisor-district-results-panel">
      {anchorListing ? (
        <div className="rounded-[20px] border border-[#D4AF37]/25 bg-[#fff7e5] px-4 py-4 text-sm leading-7 text-[#5f5a4e]" data-testid="signature-advisor-anchor-listing-banner">
          Anchored to <strong>{anchorListing.title}</strong> in {anchorListing.district}. These are the strongest similar units currently available in the unified inventory.
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[792px]" data-testid="signature-advisor-investor-thesis-shell">
        <AdvisorInvestorThesisPanel thesis={investorThesis} />
      </div>

      <div className="mx-auto w-full max-w-[804px]">
        <div className="grid gap-5 xl:grid-cols-[0.92fr_1.08fr]" data-testid="signature-advisor-phase2-top-grid">
          <div className="space-y-5">
            <AdvisorMemoryProfileCard profile={advisorProfile} />
            <AdvisorShortlistCuratorCard curator={shortlistCurator} />
          </div>
          <AdvisorDistrictIntelligencePanel intelligence={districtIntelligence} />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[804px]" data-testid="signature-advisor-comparison-strategy-shell">
        <AdvisorComparisonStrategyPanel strategy={comparisonStrategy} />
      </div>

      <div className="mx-auto w-full max-w-[804px]" data-testid="signature-advisor-investment-memo-shell">
        <AdvisorInvestmentMemoPanel
          hasCapturedLead={Boolean(capturedLead)}
          isSending={leadSubmitting}
          memo={investmentMemo}
          onOpenSampleMemo={() => window.open(`${API_BASE}/ai/signature-advisor/sample-memo.pdf`, "_blank", "noopener,noreferrer")}
          onSendMemo={openMemoLeadGate}
        />
      </div>

      <div className="mx-auto w-full max-w-[804px]" data-testid="signature-advisor-phase3-grid-shell">
        <div className="grid gap-5 xl:grid-cols-[1fr_0.95fr]" data-testid="signature-advisor-phase3-grid">
          <AdvisorScenarioSimulatorPanel activeScenarioKey={selectedScenarioKey} onSelectScenario={setSelectedScenarioKey} simulator={scenarioSimulator} />
          <AdvisorConciergeHandoffPanel
            handoff={conciergeHandoff}
            handoffPrepared={conciergePrepared}
            isPreparing={conciergeSubmitting}
            onPrepareHandoff={openConciergeHandoff}
            whatsappHref={conciergePrepared && (preparedHandoffMessage || conciergeHandoff?.whatsapp_message) ? getWhatsAppHref(preparedHandoffMessage || conciergeHandoff?.whatsapp_message) : ""}
          />
        </div>
      </div>

      {inventoryLoading ? (
        <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-5 text-sm text-[#6b6252]" data-testid="signature-advisor-inventory-loading-state">
          Loading the live district shortlist...
        </div>
      ) : null}
      {starBuys.length ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {starBuys.map((listing) => <ListingShowcaseCard actionPrefix="signature-advisor-star-buy" key={listing.id} listing={listing} />)}
        </div>
      ) : (
        <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-5 text-sm text-[#6b6252]" data-testid="signature-advisor-empty-district-results">
          I’m not seeing live shortlist candidates in that district right now. Please try another district or ask a direct question below.
        </div>
      )}

      {starBuys.length && !revealedMode ? (
        anchorListing ? (
          <div className="sticky bottom-0 z-10 -mx-1 border-t border-[#ead9ae] bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7] to-[rgba(253,251,247,0.96)] px-1 pb-1 pt-4" data-testid="signature-advisor-discovery-actions-shell">
            <div className="grid gap-3" data-testid="signature-advisor-discovery-actions">
              <ActionGridButton label="⚖️ View side-by-side comparison" onClick={() => openLeadGate("compare")} testId="signature-advisor-compare-projects-button" variant="emphasis" />
            </div>
          </div>
        ) : (
          <div className="sticky bottom-0 z-10 -mx-1 border-t border-[#ead9ae] bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7] to-[rgba(253,251,247,0.96)] px-1 pb-1 pt-4" data-testid="signature-advisor-discovery-actions-shell">
            <div className="grid gap-3 sm:grid-cols-2" data-testid="signature-advisor-discovery-actions">
              <ActionGridButton label="🔍 Find similar projects" onClick={() => openLeadGate("similar")} testId="signature-advisor-find-similar-button" variant="emphasis" />
              <ActionGridButton label="⚖️ Compare these projects" onClick={() => openLeadGate("compare")} testId="signature-advisor-compare-projects-button" variant="emphasis" />
            </div>
          </div>
        )
      ) : null}

      {revealedMode === "similar" ? (
        <div className="space-y-4" data-testid="signature-advisor-revealed-similar-projects-panel">
          <div className="rounded-[20px] border border-[#D4AF37]/25 bg-[#fff7e5] px-4 py-4 text-sm leading-7 text-[#5f5a4e]">
            Your alternative shortlist is now unlocked. These projects sit closest to your selected district strategy and are ready for deeper review.
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            {similarProjects.map((listing) => <ListingShowcaseCard actionPrefix="signature-advisor-similar-project" key={listing.id} listing={listing} />)}
          </div>
          <div className="sticky bottom-0 z-10 -mx-1 border-t border-[#ead9ae] bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7] to-[rgba(253,251,247,0.96)] px-1 pb-1 pt-4" data-testid="signature-advisor-schedule-viewing-shell">
            <ActionGridButton label="📅 Schedule a Private Viewing" onClick={beginViewingRequest} testId="signature-advisor-schedule-viewing-button" variant="solid" />
          </div>
        </div>
      ) : null}

      {revealedMode === "compare" ? (
        <div className="space-y-4" data-testid="signature-advisor-revealed-comparison-panel">
          <AdvisorComparisonStrategyPanel strategy={comparisonStrategy} />
          <div className="grid gap-3 xl:grid-cols-4">
            {comparisonInsights.map((item) => (
              <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4" data-testid={`signature-advisor-comparison-card-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{item.label}</p>
                <p className="mt-3 text-sm leading-7 text-[#1A1A1A]">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4" data-testid="signature-advisor-comparison-table">
            <div className="grid grid-cols-[1.2fr_0.8fr_0.7fr_0.7fr] gap-3 border-b border-[#efe4ce] pb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">
              <span>Project</span>
              <span>Entry Price</span>
              <span>PSF</span>
              <span>Size</span>
            </div>
            <div className="mt-3 space-y-3">
              {[anchorListing, ...starBuys].filter(Boolean).map((listing) => (
                <div className="grid grid-cols-[1.2fr_0.8fr_0.7fr_0.7fr] gap-3 text-sm text-[#1A1A1A]" data-testid={`signature-advisor-comparison-row-${listing.id}`} key={listing.id}>
                  <span>{listing.title}</span>
                  <span>{formatListingPrice(listing)}</span>
                  <span>{formatCurrency(listing.psf || 0, 0)}</span>
                  <span>{formatNumber(listing.sqft || 0)} sqft</span>
                </div>
              ))}
            </div>
          </div>
          <div className="sticky bottom-0 z-10 -mx-1 border-t border-[#ead9ae] bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7] to-[rgba(253,251,247,0.96)] px-1 pb-1 pt-4" data-testid="signature-advisor-schedule-viewing-shell">
            <ActionGridButton label="📅 Schedule a Private Viewing" onClick={beginViewingRequest} testId="signature-advisor-schedule-viewing-button" variant="solid" />
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderLeadForm = () => (
    <form className="space-y-3" data-testid="signature-advisor-lead-form" onSubmit={submitLeadCapture}>
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-lead-name-input" onChange={(event) => setLeadForm((current) => ({ ...current, name: event.target.value }))} placeholder="Name" required type="text" value={leadForm.name} />
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-lead-whatsapp-input" onChange={(event) => setLeadForm((current) => ({ ...current, whatsapp: event.target.value }))} placeholder="Contact No (WhatsApp)" required type="text" value={leadForm.whatsapp} />
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-lead-email-input" onChange={(event) => setLeadForm((current) => ({ ...current, email: event.target.value }))} placeholder="Email ID" required type="email" value={leadForm.email} />
      <button className="w-full rounded-[16px] bg-[#D4AF37] px-4 py-3 text-sm font-bold text-[#1A1A1A]" data-testid="signature-advisor-lead-submit-button" disabled={leadSubmitting} type="submit">
        {leadSubmitting
          ? leadContext?.mode === "concierge"
            ? "Preparing handoff..."
            : leadContext?.mode === "viewing"
              ? "Saving your details..."
            : leadContext?.mode === "memo"
              ? "Sending your memo..."
              : "Sending your PDF..."
          : leadContext?.mode === "concierge"
            ? "Prepare concierge handoff"
            : leadContext?.mode === "viewing"
              ? "Continue to viewing details"
            : leadContext?.mode === "memo"
              ? "Send my memo PDF"
              : "Unlock insights & send PDF"}
      </button>
    </form>
  );

  const renderListingWarmLoading = () => (
    <div className="rounded-[22px] border border-[#D4AF37]/25 bg-white px-4 py-5 text-sm text-[#6b6252]" data-testid="signature-advisor-listing-handoff-loading">
      <div className="flex items-center gap-3">
        <LoaderCircle className="h-4 w-4 animate-spin text-[#8b6d16]" />
        Curating your Premium Comparison shortlist...
      </div>
    </div>
  );

  const renderPremiumComparisonReady = () => (
    <div className="space-y-4" data-testid="signature-advisor-premium-comparison-ready">
      <div className="rounded-[22px] border border-[#D4AF37]/25 bg-[#fff7e5] px-4 py-4 text-sm leading-7 text-[#5f5a4e]">
        Your Premium Comparison is ready. Open the curated 3-card grid to review the strongest matched opportunities side by side.
      </div>
      <ActionGridButton
        label="✨ Open Premium Comparison"
        onClick={() => window.dispatchEvent(new CustomEvent("signature-advisor-open-comparison-modal", { detail: { listingId: anchorListing?.id, listings: premiumComparisonProjects } }))}
        testId="signature-advisor-open-premium-comparison-button"
        variant="solid"
      />
    </div>
  );

  const renderPostModalActions = () => (
    <div className="space-y-4" data-testid="signature-advisor-post-modal-actions">
      <div className="rounded-[22px] border border-[#D4AF37]/25 bg-[#fff7e5] px-4 py-4 text-sm leading-7 text-[#5f5a4e]">
        Your comparison is ready whenever you need it. If you would like to move ahead, you can request a private viewing or message our team directly.
      </div>
      <ActionGridButton label="📅 Request Viewing" onClick={beginViewingRequest} testId="signature-advisor-post-modal-request-viewing-button" variant="solid" />
    </div>
  );

  const renderTaxForm = () => (
    <div className="space-y-3" data-testid="signature-advisor-tax-form">
      <select className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-tax-residency-select" onChange={(event) => setTaxForm((current) => ({ ...current, residency: event.target.value }))} value={taxForm.residency}>
        <option value="SC">Singapore Citizen (SC)</option>
        <option value="PR">Permanent Resident (PR)</option>
        <option value="FR">Foreigner (FR)</option>
      </select>
      <select className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-tax-property-count-select" onChange={(event) => setTaxForm((current) => ({ ...current, property_count: event.target.value }))} value={taxForm.property_count}>
        <option value={1}>1 property</option>
        <option value={2}>2 properties</option>
        <option value={3}>3+ properties</option>
      </select>
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-tax-purchase-price-input" onChange={(event) => setTaxForm((current) => ({ ...current, purchase_price: event.target.value }))} placeholder="Indicative purchase price (optional)" type="number" value={taxForm.purchase_price} />
      <ActionGridButton label="Show 2026 tax view" onClick={submitTaxSnapshot} testId="signature-advisor-tax-submit-button" variant="solid" />
    </div>
  );

  const renderTaxResult = () => (
    <div className="space-y-3" data-testid="signature-advisor-tax-result">
      <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm leading-7 text-[#1A1A1A]">
        <p><strong>ABSD Rate:</strong> {taxSnapshot?.absdRate}%</p>
        <p><strong>BSD Positioning:</strong> Progressive up to 6% above SGD 3M</p>
        {Number(taxForm.purchase_price || 0) > 0 ? <p><strong>Indicative BSD:</strong> {formatCurrency(taxSnapshot?.bsdDuty || 0)}</p> : null}
      </div>
      <ActionGridButton label="Unlock Tax Optimization PDF" onClick={unlockTaxReport} testId="signature-advisor-tax-unlock-button" variant="solid" />
    </div>
  );

  const renderRentForm = () => (
    <div className="space-y-3" data-testid="signature-advisor-rent-form">
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-rent-amount-input" onChange={(event) => setRentForm((current) => ({ ...current, monthly_rent: event.target.value }))} placeholder="Current monthly rent" type="number" value={rentForm.monthly_rent} />
      <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-buy-budget-input" onChange={(event) => setRentForm((current) => ({ ...current, budget: event.target.value }))} placeholder="Target purchase budget" type="number" value={rentForm.budget} />
      <ActionGridButton label="Show cost of waiting" onClick={submitRentVsBuy} testId="signature-advisor-rent-submit-button" variant="solid" />
    </div>
  );

  const renderRentResult = () => (
    <div className="space-y-3" data-testid="signature-advisor-rent-result">
      <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm leading-7 text-[#1A1A1A]">
        <p><strong>Ownership range:</strong> {formatCurrency(rentSnapshot?.lowRatePayment || 0)} to {formatCurrency(rentSnapshot?.highRatePayment || 0)} / month</p>
        <p><strong>Annual rent outflow:</strong> {formatCurrency(rentSnapshot?.annualRentSpend || 0)}</p>
        <p><strong>2026 rates:</strong> 2.8%–3.2%</p>
      </div>
      <ActionGridButton label="Unlock Wealth Accumulation Report" onClick={unlockWealthReport} testId="signature-advisor-rent-unlock-button" variant="solid" />
    </div>
  );

  const renderViewingProjectPicker = () => (
    <div className="space-y-4" data-testid="signature-advisor-viewing-project-picker">
      {viewingProjectOptions.length ? (
        <div className="grid gap-3">
          {viewingProjectOptions.map((listing) => (
            <ActionGridButton key={listing.id} label={listing.title} onClick={() => chooseViewingProject(listing)} testId={`signature-advisor-viewing-project-${listing.id}`} />
          ))}
        </div>
      ) : (
        <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4 text-sm leading-7 text-[#5f5a4e]" data-testid="signature-advisor-viewing-project-empty-state">
          I’m not seeing a live project shortlist yet, but you can still continue with a general private viewing request.
        </div>
      )}
      <ActionGridButton
        label={selectedDistrict?.label ? `Continue without a specific listing (${selectedDistrict.label})` : "Continue without a specific listing"}
        onClick={() => chooseViewingProject({
          id: `general-viewing-request-${selectedDistrict?.code || "singapore"}`,
          title: selectedDistrict?.label ? `Private viewing request in ${selectedDistrict.label}` : "Private viewing request",
          district: selectedDistrict?.label || "Singapore",
          category: "buy",
          property_type: "Property",
          status: "Requested",
        })}
        testId="signature-advisor-viewing-project-generic-button"
        variant="outline"
      />
    </div>
  );

  const renderViewingForm = () => (
    <form className="space-y-3" data-testid="signature-advisor-viewing-form" onSubmit={submitViewingRequest}>
      <div className="rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4" data-testid="signature-advisor-calendar-step">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Step A — Choose a date</p>
        <Calendar
          className="mx-auto mt-3 w-fit rounded-[18px] bg-[#FDFBF7]"
          data-testid="signature-advisor-viewing-calendar"
          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
          mode="single"
          onSelect={(date) =>
            setSchedulingRequest((current) => ({
              ...current,
              selectedDate: date || null,
              preferred_date: date ? toIsoDate(date) : "",
              time_window: "",
              specific_time: "",
              custom_time: "",
            }))
          }
          selected={schedulingRequest.selectedDate || undefined}
        />
      </div>

      {schedulingRequest.preferred_date ? (
        <div className="space-y-3 rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4" data-testid="signature-advisor-time-step">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Step B — Choose time</p>
          <div className="grid grid-cols-2 gap-3">
            {["AM", "PM"].map((slot) => (
              <ActionGridButton key={slot} label={slot} onClick={() => setSchedulingRequest((current) => ({ ...current, time_window: slot, specific_time: "", custom_time: "" }))} testId={`signature-advisor-viewing-time-${slot.toLowerCase()}-button`} variant={schedulingRequest.time_window === slot ? "solid" : "outline"} />
            ))}
          </div>
          {schedulingRequest.time_window ? (
            <div className="space-y-3 rounded-[18px] border border-[#D4AF37]/20 bg-[#FDFBF7] px-3 py-3" data-testid="signature-advisor-viewing-slot-picker">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">Hourly time slots</p>
              <div className="grid grid-cols-2 gap-3">
                {VIEWING_TIME_SLOTS[schedulingRequest.time_window].map((slot) => (
                  <ActionGridButton key={slot} label={slot} onClick={() => setSchedulingRequest((current) => ({ ...current, specific_time: slot, custom_time: "" }))} testId={`signature-advisor-viewing-specific-time-${slot.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-button`} variant={schedulingRequest.specific_time === slot && !schedulingRequest.custom_time ? "solid" : "outline"} />
                ))}
              </div>
              <input className="w-full rounded-[16px] border border-[#D4AF37]/30 bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none" data-testid="signature-advisor-viewing-custom-time-input" onChange={(event) => setSchedulingRequest((current) => ({ ...current, custom_time: event.target.value, specific_time: event.target.value ? "" : current.specific_time }))} placeholder="Or enter a custom time (e.g. 6:30 PM)" type="text" value={schedulingRequest.custom_time} />
            </div>
          ) : null}
        </div>
      ) : null}
      <button className="w-full rounded-[16px] bg-[#D4AF37] px-4 py-3 text-sm font-bold text-[#1A1A1A]" data-testid="signature-advisor-viewing-submit-button" disabled={viewingSubmitting} type="submit">
        {viewingSubmitting ? "Logging request..." : "Confirm private viewing request"}
      </button>
    </form>
  );

  const renderViewingSuccess = () => (
    <div className="space-y-4" data-testid="signature-advisor-viewing-success-panel">
      <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-5 text-sm leading-7 text-emerald-900" data-testid="signature-advisor-viewing-success-message">
        {viewingSuccessMessage || "Your private viewing request has been submitted successfully."}
      </div>
      <ActionGridButton label="Done" onClick={() => setPanelMode("entry")} testId="signature-advisor-viewing-success-done-button" variant="solid" />
    </div>
  );

  const renderPanelContent = () => {
    if (panelMode === "entry") return renderEntryButtons();
    if (panelMode === "segment") return renderSegmentPicker();
    if (panelMode === "districts") return renderDistrictPicker();
    if (panelMode === "district-results") return renderDistrictResults();
    if (panelMode === "listing-handoff-loading") return renderListingWarmLoading();
    if (panelMode === "lead-form") return renderLeadForm();
    if (panelMode === "premium-comparison-ready") return renderPremiumComparisonReady();
    if (panelMode === "post-modal-actions") return renderPostModalActions();
    if (panelMode === "tax-form") return renderTaxForm();
    if (panelMode === "tax-result") return renderTaxResult();
    if (panelMode === "rent-form") return renderRentForm();
    if (panelMode === "rent-result") return renderRentResult();
    if (panelMode === "viewing-project") return renderViewingProjectPicker();
    if (panelMode === "viewing-form") return renderViewingForm();
    if (panelMode === "viewing-success") return renderViewingSuccess();
    return null;
  };

  const inlinePanelContent = INLINE_PANEL_MODES.has(panelMode) ? renderPanelContent() : null;
  const journeyPopupMeta = getJourneyPopupMeta(panelMode, selectedDistrict?.label, districtIntroMessage, leadContext);
  const isWidgetOpen = forceRouteExperience ? true : open;
  const isJourneyPopupOpen = isWidgetOpen && JOURNEY_POPUP_PANEL_MODES.has(panelMode);
  const closeJourneyPopup = () => setPanelMode("entry");
  const handleClose = () => {
    if (forceRouteExperience) {
      navigate(buildBrandedPath("/"));
      return;
    }
    setOpen(false);
  };
  useEffect(() => {
    if (forceRouteExperience || typeof window === "undefined") {
      return undefined;
    }

    const resolveVisibleRect = (selector) => {
      const element = document.querySelector(selector);
      if (!element) return null;
      const style = window.getComputedStyle(element);
      if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return null;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return rect;
    };

    const updateLayout = () => {
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        setMobileLauncherLayout({ anchor: "right", bottomOffset: 24, isMobile: false });
        return;
      }

      const navRect = resolveVisibleRect('[data-testid="mobile-bottom-navigation"]');
      const rightOverlayRect = resolveVisibleRect('[data-testid="sticky-signup-3d-bot-widget"]');
      const leftOverlayRect = resolveVisibleRect('[data-testid="floating-ownership-bot"]');

      let anchor = "right";
      if (rightOverlayRect && !leftOverlayRect) {
        anchor = "left";
      } else if (leftOverlayRect && !rightOverlayRect) {
        anchor = "right";
      } else if (leftOverlayRect && rightOverlayRect) {
        anchor = "center";
      } else if (!navRect) {
        anchor = "center";
      }

      const bottomClearanceCandidates = [16];
      if (navRect) bottomClearanceCandidates.push(window.innerHeight - navRect.top + 12);
      if (rightOverlayRect) bottomClearanceCandidates.push(window.innerHeight - rightOverlayRect.top + 12);
      if (leftOverlayRect) bottomClearanceCandidates.push(window.innerHeight - leftOverlayRect.top + 12);

      setMobileLauncherLayout({
        anchor,
        bottomOffset: Math.max(...bottomClearanceCandidates),
        isMobile: true,
      });
    };

    const handleViewportChange = () => window.requestAnimationFrame(updateLayout);
    updateLayout();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { passive: true });
    const observer = new MutationObserver(handleViewportChange);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"], childList: true, subtree: true });

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange);
      observer.disconnect();
    };
  }, [forceRouteExperience]);
  const rootClassName = forceRouteExperience
    ? "relative z-[30] w-full font-[Montserrat,sans-serif]"
    : mobileLauncherLayout.isMobile
      ? `fixed z-[70] font-[Montserrat,sans-serif] ${mobileLauncherLayout.anchor === "left" ? "left-3 right-auto w-auto" : mobileLauncherLayout.anchor === "center" ? "left-1/2 w-[calc(100vw-1rem)] -translate-x-1/2" : "right-3 left-auto w-auto"}`
      : "fixed bottom-6 right-6 z-[70] w-auto font-[Montserrat,sans-serif]";
  const rootInlineStyle = !forceRouteExperience && mobileLauncherLayout.isMobile ? { bottom: `${mobileLauncherLayout.bottomOffset}px` } : undefined;
  const launcherButtonClassName = `inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#f6e3a7] bg-[#D4AF37] text-[#1A1A1A] shadow-[0_20px_40px_rgba(212,175,55,0.38)] ${!forceRouteExperience && mobileLauncherLayout.isMobile && mobileLauncherLayout.anchor === "center" ? "ml-auto" : ""}`;
  const panelClassName = forceRouteExperience
    ? "flex h-[calc(100dvh-13rem)] w-full max-w-none flex-col overflow-hidden rounded-[30px] border border-[#D4AF37]/35 bg-[#F6F4EB] shadow-[0_24px_80px_rgba(0,25,53,0.22)] md:h-[calc(100dvh-15rem)]"
    : "flex h-[min(86vh,836px)] w-full max-w-[420px] flex-col overflow-hidden rounded-[30px] border border-[#D4AF37]/35 bg-[#F6F4EB] shadow-[0_24px_80px_rgba(0,25,53,0.22)]";
  const journeyPopupClassName = forceRouteExperience
    ? "fixed inset-x-3 top-[5.5rem] z-[120] flex h-[calc(100dvh-7rem)] flex-col overflow-hidden rounded-[30px] border border-[#D4AF37]/30 bg-[#FDFBF7] shadow-[0_24px_80px_rgba(0,25,53,0.18)] md:absolute md:inset-auto md:bottom-0 md:right-0 md:h-[min(90vh,920px)] md:w-[min(94vw,880px)] md:max-w-[880px]"
    : "absolute bottom-0 right-0 z-[120] flex h-[min(90vh,920px)] w-[min(94vw,880px)] max-w-[880px] flex-col overflow-hidden rounded-[30px] border border-[#D4AF37]/30 bg-[#FDFBF7] shadow-[0_24px_80px_rgba(0,25,53,0.18)] max-sm:bottom-[calc(100%+12px)] max-sm:h-[min(85vh,920px)] sm:right-[calc(100%+16px)] sm:top-0 sm:bottom-auto";

  return (
    <div className={rootClassName} data-mobile-launcher-anchor={mobileLauncherLayout.anchor} data-testid="signature-advisor-widget-root" style={rootInlineStyle}>
      {isWidgetOpen ? (
        <div className="relative" data-testid="signature-advisor-open-shell">
          {isJourneyPopupOpen ? (
            <div className={journeyPopupClassName} data-testid="signature-advisor-journey-popup">
              <div className="border-b border-[#D4AF37]/20 bg-[#fff7e5] px-6 py-5" data-testid="signature-advisor-journey-popup-header">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-left">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]" data-testid="signature-advisor-journey-popup-eyebrow">{journeyPopupMeta.eyebrow}</p>
                    <h3 className="mt-2 text-2xl font-semibold text-[#001935]" data-testid="signature-advisor-journey-popup-title">{journeyPopupMeta.title}</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5f5a4e]" data-testid="signature-advisor-journey-popup-description">{journeyPopupMeta.description}</p>
                  </div>

                  <button className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-white text-[#8b6d16] shadow-sm transition hover:bg-[#fff4db]" data-testid="signature-advisor-journey-popup-close-button" onClick={closeJourneyPopup} type="button">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6" data-testid="signature-advisor-journey-popup-content">
                {renderPanelContent()}
              </div>
            </div>
          ) : null}

          <div className={panelClassName} data-testid="signature-advisor-panel">
            <div className="border-b border-[#D4AF37]/20 bg-[#fff7e5] px-4 py-4">
              <div className="flex items-center gap-3" data-testid="signature-advisor-header-branding">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-transparent" data-testid="signature-advisor-header-logo">
                  <House className="h-6 w-6 text-[#D4AF37]" strokeWidth={2.4} />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8b6d16]">SG PROP PORTAL</p>
                  <h3 className="text-xl font-semibold text-[#1A1A1A]">The Signature Advisor</h3>
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="signature-advisor-messages-panel" ref={messagesPanelRef}>
              {messages.map((item, index) => <MessageBubble index={index} item={item} key={`${item.role}-${index}`} />)}
              {inlinePanelContent}
            </div>

            {inputUnlocked && panelMode !== "lead-form" && panelMode !== "viewing-form" ? (
              <div className="border-t border-[#D4AF37]/20 bg-white px-4 py-4" data-testid="signature-advisor-input-shell">
                <div className="flex items-end gap-3">
                  <textarea className="min-h-[84px] flex-1 rounded-[18px] border border-[#D4AF37]/30 bg-[#FDFBF7] px-4 py-3 text-[16px] text-[#1A1A1A] outline-none" data-testid="signature-advisor-input" onChange={(event) => setPrompt(event.target.value)} placeholder="Ask a specific wealth strategy question..." value={prompt} />
                  <button className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37] text-[#1A1A1A]" data-testid="signature-advisor-send-button" disabled={sending || booting} onClick={() => sendPrompt()} type="button">
                    {sending || booting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ) : null}

          {objectionResponse ? <AdvisorObjectionActionBar objectionResponse={objectionResponse} onSelectAction={handleObjectionQuickAction} /> : null}

            <div className="grid grid-cols-2 gap-3 border-t border-[#D4AF37]/20 bg-[#fff7e5] px-4 py-4" data-testid="signature-advisor-bottom-bar">
              <a className="flex items-center justify-center gap-2 rounded-[16px] border border-[#D4AF37]/35 bg-white px-4 py-3 text-sm font-semibold text-[#8b6d16]" data-testid="signature-advisor-whatsapp-button" href={getWhatsAppHref("Hello Team SG Prop Portal, I would like to speak directly regarding a property opportunity.")} rel="noreferrer" target="_blank">
                <MessageCircle className="h-4 w-4" /> WhatsApp the Team
              </a>
              <button className="flex items-center justify-center gap-2 rounded-[16px] bg-[#D4AF37] px-4 py-3 text-sm font-bold text-[#1A1A1A]" data-testid="signature-advisor-bottom-close-button" onClick={handleClose} type="button">
                <X className="h-4 w-4" /> {forceRouteExperience ? "Back" : "Close"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!hideFloatingLauncher ? (
        <button className={launcherButtonClassName} data-testid="signature-advisor-launcher-button" onClick={() => setOpen(true)} type="button">
          <Bot className="h-7 w-7" />
        </button>
      ) : null}
    </div>
  );
};